import { useEffect, useState, type ReactNode } from 'react';
import {
  messages,
  type Grower,
  type HarvestWeek,
  type UserFarmRow,
  type MasterData,
  type Season,
} from '@fruitways/shared';
import { ApiRequestError, NetworkError } from '@/api/client';
import { Icon } from '@/components';
import { pullAdminData } from '@/db/sync';
import { useAdminHarvestWeeks, useAdminUserFarms, useGrowers } from '@/hooks/useAdmin';
import { seasonForToday, useMasterData } from '@/hooks/useMasterData';
import { useOnline } from '@/hooks/useOnline';

export const t = messages.admin;

let refreshedThisSession = false;

export interface AdminData {
  master: MasterData | undefined;
  growers: Grower[] | undefined;
  harvestWeeks: HarvestWeek[] | undefined;
  userFarms: UserFarmRow[] | undefined;
  season: Season | undefined;
  /** True until the phone has a copy of the admin tables. */
  loading: boolean;
  /** Set when the first load failed and nothing is on the phone yet. */
  loadError: string | null;
}

/**
 * Master data screens read from the phone and refresh from the pack house once per session,
 * so an admin can still look things up with no signal. Changes always need signal.
 */
export function useAdminData(): AdminData {
  const master = useMasterData();
  const growers = useGrowers();
  const harvestWeeks = useAdminHarvestWeeks();
  const userFarms = useAdminUserFarms();
  const { online } = useOnline();
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (refreshedThisSession || !online) return;
    refreshedThisSession = true;
    pullAdminData().catch(() => {
      refreshedThisSession = false;
      setLoadError(t.data.loadFailed);
    });
  }, [online]);

  const loading = !master || !growers || !harvestWeeks || !userFarms;
  return {
    master,
    growers,
    harvestWeeks,
    userFarms,
    season: seasonForToday(master),
    loading,
    loadError: loading ? (loadError ?? (online ? null : t.data.loadFailed)) : null,
  };
}

/** Copy for a failed admin change. States the fix, never the fault. */
export function adminErrorMessage(err: unknown): string {
  if (err instanceof NetworkError) return t.data.saveFailed;
  if (err instanceof ApiRequestError && (err.code === 'conflict' || err.code === 'not_found')) {
    return t.data.changedElsewhere;
  }
  return messages.errors.generic;
}

export function InlineError({ children }: { children: ReactNode }) {
  return (
    <span className="fw-inline-error" role="alert">
      <Icon name="alert-circle" size={16} style={{ marginTop: '2px' }} />
      {children}
    </span>
  );
}

export function InlineNote({ children }: { children: ReactNode }) {
  return (
    <span className="fw-inline-note">
      <Icon name="information-circle-outline" size={16} style={{ marginTop: '2px' }} />
      <span>{children}</span>
    </span>
  );
}
